import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { useIntl, FormattedMessage } from 'react-intl'
import { CapeeshColors } from '../../../../../assets/ColorPalette'
import useAudioPlayer from '../hooks/useAudioPlayer'
import { useIsTouchDevice } from '../hooks/useIsPhone'
import L2OnScreenKeyboard from './L2OnScreenKeyboard'
import renderFormattedText from '../utils/renderFormattedText'
import { playerMessages } from '../CapeeshPlayer.i18n.Messages'

const AudioPlayType = {
    DontPlay: 0,
    PlayBefore: 1,
    PlayAfter: 2,
}

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '8px 0',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },

    sentenceContainer: {
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 16,
        padding: '20px 20px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 4,
        lineHeight: '200%',
        boxSizing: 'border-box',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    sentenceWord: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 400,
        color: '#FFFFFF',
    },
    blankSlot: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 80,
        padding: '4px 12px',
        borderRadius: 8,
        borderBottom: '2px solid rgba(255, 255, 255, 0.4)',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        color: '#FFFFFF',
        minHeight: 32,
        transition: 'all 200ms ease',
    },
    blankSlotActive: {
        borderColor: CapeeshColors.BlueBright,
        backgroundColor: 'rgba(90, 180, 241, 0.1)',
        boxShadow: '0 0 0 1px rgba(90, 180, 241, 0.3)',
    },
    blankSlotFilled: {
        borderColor: CapeeshColors.BlueBright,
        backgroundColor: 'rgba(90, 180, 241, 0.15)',
    },
    blankSlotCorrect: {
        borderColor: CapeeshColors.GreenBright,
        backgroundColor: 'rgba(128, 211, 135, 0.2)',
    },
    blankSlotIncorrect: {
        borderColor: CapeeshColors.PinkBright,
        backgroundColor: 'rgba(255, 102, 110, 0.2)',
    },
    blankSlotExam: {
        borderColor: 'rgba(255, 255, 255, 0.45)',
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },

    inlineInput: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 80,
        padding: '4px 12px',
        borderRadius: 8,
        border: 'none',
        borderBottom: `2px solid ${CapeeshColors.BlueBright}`,
        backgroundColor: 'rgba(90, 180, 241, 0.1)',
        boxShadow: '0 0 0 1px rgba(90, 180, 241, 0.3)',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        color: '#FFFFFF',
        minHeight: 32,
        textAlign: 'center',
        outline: 'none',
        caretColor: '#FFFFFF',
        boxSizing: 'content-box',
        transition: 'border-color 200ms ease, box-shadow 200ms ease, background-color 200ms ease',
    },
    '@keyframes inputShake': {
        '0%, 100%': { transform: 'translateX(0)' },
        '20%': { transform: 'translateX(-4px)' },
        '40%': { transform: 'translateX(4px)' },
        '60%': { transform: 'translateX(-3px)' },
        '80%': { transform: 'translateX(2px)' },
    },
    inlineInputWrong: {
        animation: '$inputShake 400ms ease',
        borderBottom: `2px solid ${CapeeshColors.PinkBright}`,
        boxShadow: `0 0 0 1px ${CapeeshColors.PinkBright}`,
        backgroundColor: 'rgba(255, 102, 110, 0.12)',
    },
    revealedHint: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center',
        letterSpacing: '0.5px',
    },
    revealedWord: {
        color: '#FFFFFF',
        fontWeight: 600,
    },

    writeSubmitRow: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
    },
    writeSubmitButton: {
        padding: '12px 36px',
        borderRadius: 20,
        border: 'none',
        backgroundColor: CapeeshColors.BlueBright,
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:disabled': {
            opacity: 0.4,
            cursor: 'default',
        },
        '&:hover:not(:disabled)': {
            transform: 'translateY(-1px)',
        },
    },

    helperKeyboard: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        justifyContent: 'center',
        width: '100%',
    },
    helperKey: {
        minWidth: 40,
        height: 40,
        padding: '0 6px',
        borderRadius: 8,
        border: '1px solid rgba(255, 255, 255, 0.25)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 150ms ease',
        userSelect: 'none',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.22)',
            borderColor: 'rgba(255, 255, 255, 0.4)',
        },
        '&:active': {
            transform: 'scale(0.93)',
        },
    },

    chipsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'center',
        width: '100%',
    },
    chip: {
        padding: '10px 18px',
        borderRadius: 20,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.16)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
        },
    },
    chipUsed: {
        opacity: 0.3,
        cursor: 'default',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    chipCorrect: {
        borderColor: CapeeshColors.GreenBright,
        backgroundColor: 'rgba(128, 211, 135, 0.2)',
    },
    chipIncorrect: {
        borderColor: CapeeshColors.PinkBright,
        backgroundColor: 'rgba(255, 102, 110, 0.2)',
    },
    chipExamSelected: {
        borderColor: 'rgba(255, 255, 255, 0.45)',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },

    '@keyframes chipShake': {
        '0%, 100%': { transform: 'translateX(0)' },
        '20%': { transform: 'translateX(-5px)' },
        '40%': { transform: 'translateX(5px)' },
        '60%': { transform: 'translateX(-3px)' },
        '80%': { transform: 'translateX(3px)' },
    },
    chipFlashWrong: {
        animation: '$chipShake 400ms ease',
        borderColor: `${CapeeshColors.PinkBright} !important`,
        backgroundColor: 'rgba(255, 102, 110, 0.2) !important',
        pointerEvents: 'none',
    },

    examButtonRow: {
        display: 'flex',
        gap: 12,
        justifyContent: 'center',
        width: '100%',
    },
    resetButton: {
        padding: '12px 28px',
        borderRadius: 20,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'transparent',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
    },
    submitButton: {
        padding: '12px 36px',
        borderRadius: 20,
        border: 'none',
        backgroundColor: 'rgba(255, 255, 255, 0.35)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            backgroundColor: 'rgba(255, 255, 255, 0.45)',
        },
    },

    feedbackText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'center',
    },
    feedbackCorrect: {
        color: CapeeshColors.GreenBright,
    },
    feedbackIncorrect: {
        color: CapeeshColors.PinkBright,
    },

    questionText: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        color: '#FFFFFF',
        textAlign: 'center',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        maxWidth: '100%',
    },
}))

/**
 * FillInBlankInteraction — supports both writing mode (type 2) and distractor mode (type 3).
 *
 * Blanks are positioned using the startIndex field on each alternative, which indicates where
 * in testOption.text the word appears. Alternatives sharing the same startIndex are grouped
 * as options for the same blank (one correct + distractors). Multiple blanks are filled sequentially.
 */
const FillInBlankInteraction = ({
    testOption,
    onComplete,
    isExamMode,
    audioMap,
    l2LanguageId,
    deferAudioPlayBefore,
    textAlign = 'center',
    isNativeSpeaker = false,
}) => {
    const classes = useStyles()
    const intl = useIntl()
    const audioPlayer = useAudioPlayer()
    const isTouchDevice = useIsTouchDevice()

    const isL2 = l2LanguageId != null && testOption.textLanguageId === l2LanguageId
    const audioPlayType = testOption.audioPlayType ?? AudioPlayType.DontPlay
    const audioBlobUrl = testOption.textAudioUrl && audioMap ? audioMap[testOption.textAudioUrl] : null
    const shouldPlayBefore = isL2 && audioBlobUrl && audioPlayType === AudioPlayType.PlayBefore
    const shouldPlayAfter = isL2 && audioBlobUrl && audioPlayType === AudioPlayType.PlayAfter

    // When deferred, show interaction immediately (audio plays after display)
    const [ready, setReady] = useState(!shouldPlayBefore || deferAudioPlayBefore)

    const isWritingMode =
        (testOption.scenarioInteractiveElementType ?? testOption.interactiveElementType) === 2
    const alternatives = testOption.alternatives || []
    const sentenceText = testOption.text || ''

    // ── Group alternatives by startIndex to identify blank positions ──
    const { blanks, alternativesByBlank } = useMemo(() => {
        const blankMap = {}
        let hasWholeTextDistractor = false
        alternatives.forEach((alt) => {
            let si = alt.startIndex
            if (si == null) return
            // startIndex -1 means the distractor covers the whole text
            if (si === -1) {
                si = 0
                hasWholeTextDistractor = true
            } else if (si < 0) {
                return
            }
            if (!blankMap[si]) blankMap[si] = []
            blankMap[si].push(alt)
        })

        const sortedStartIndices = Object.keys(blankMap)
            .map(Number)
            .sort((a, b) => a - b)

        if (sortedStartIndices.length === 0) {
            // Fallback: no valid startIndex data — single blank with all alternatives
            return {
                blanks: [{ startIndex: -1, length: 0, correctText: '' }],
                alternativesByBlank: [alternatives],
            }
        }

        const blanks = sortedStartIndices.map((si) => {
            const group = blankMap[si]
            const correctAlt = group.find((a) => a.correct) || group[0]
            return {
                startIndex: si,
                length: hasWholeTextDistractor && si === 0
                    ? sentenceText.length
                    : correctAlt.text.length,
                correctText: correctAlt.text,
            }
        })

        const alternativesByBlank = sortedStartIndices.map((si) => {
            const alts = [...blankMap[si]]
            for (let i = alts.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1))
                ;[alts[i], alts[j]] = [alts[j], alts[i]]
            }
            return alts
        })

        return { blanks, alternativesByBlank }
    }, [alternatives, sentenceText])

    // ── Parse sentence into text segments and blank slots ──
    const sentenceParts = useMemo(() => {
        if (blanks.length === 0) {
            return [{ type: 'text', value: sentenceText }]
        }

        // Empty text but blanks exist: blank slots only (sentence builder)
        if (!sentenceText) {
            return blanks.map((_, idx) => ({ type: 'blank', blankIndex: idx }))
        }

        // Fallback: no startIndex data — look for ___ pattern or append blank
        if (blanks[0].startIndex < 0) {
            const match = sentenceText.match(/_{2,}/)
            if (match) {
                const idx = sentenceText.indexOf(match[0])
                const parts = []
                if (idx > 0) parts.push({ type: 'text', value: sentenceText.slice(0, idx) })
                parts.push({ type: 'blank', blankIndex: 0 })
                const after = sentenceText.slice(idx + match[0].length)
                if (after) parts.push({ type: 'text', value: after })
                return parts
            }
            return [
                { type: 'text', value: sentenceText },
                { type: 'blank', blankIndex: 0 },
            ]
        }

        // Build parts using startIndex positions
        const parts = []
        let cursor = 0
        blanks.forEach((blank, idx) => {
            if (blank.startIndex > cursor) {
                parts.push({ type: 'text', value: sentenceText.slice(cursor, blank.startIndex) })
            }
            parts.push({ type: 'blank', blankIndex: idx })
            cursor = blank.startIndex + blank.length
        })
        if (cursor < sentenceText.length) {
            parts.push({ type: 'text', value: sentenceText.slice(cursor) })
        }
        return parts
    }, [sentenceText, blanks])

    // ── Sentence builder detection ──
    // When all meaningful text is inside the alternatives (no text segments or only
    // whitespace between blanks), the interaction becomes a sentence builder where
    // all chips are shown at once and the user constructs the sentence piece by piece.
    const isSentenceBuilder = useMemo(() => {
        if (blanks.length <= 1) return false
        const meaningfulText = sentenceParts
            .filter((p) => p.type === 'text')
            .map((p) => p.value.trim())
            .join('')
        return meaningfulText.length === 0
    }, [blanks, sentenceParts])

    // ── Pooled & shuffled chips for sentence builder ──
    const pooledChips = useMemo(() => {
        if (!isSentenceBuilder) return []
        const chips = []
        alternativesByBlank.forEach((alts, blankIdx) => {
            alts.forEach((alt, altIdx) => {
                chips.push({ ...alt, originalBlankIndex: blankIdx, chipId: `${blankIdx}-${altIdx}` })
            })
        })
        for (let i = chips.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[chips[i], chips[j]] = [chips[j], chips[i]]
        }
        return chips
    }, [isSentenceBuilder, alternativesByBlank])

    // ── Merged words for sentence builder completion ──
    // When all tokens are placed correctly, merge fragments back into original words
    // e.g. tokens "th","ank","y","ou" → display "thank","you"
    const mergedSentenceWords = useMemo(() => {
        if (!isSentenceBuilder || !sentenceText) return []
        return sentenceText.split(/\s+/).filter((w) => w.length > 0)
    }, [isSentenceBuilder, sentenceText])

    // ── PlayBefore audio ──
    useEffect(() => {
        if (!shouldPlayBefore) return
        let cancelled = false
        if (deferAudioPlayBefore) {
            // Deferred: interaction is already visible, play audio after a short render delay
            const timer = setTimeout(() => {
                if (!cancelled) audioPlayer.playAudio(audioBlobUrl)
            }, 200)
            return () => {
                cancelled = true
                clearTimeout(timer)
                audioPlayer.stopAudio()
            }
        } else {
            // Standard: hide interaction until audio finishes
            audioPlayer.playAudio(audioBlobUrl).then(() => {
                if (!cancelled) setReady(true)
            })
            return () => {
                cancelled = true
                audioPlayer.stopAudio()
            }
        }
    }, [])

    // ── State ──
    const [blankAnswers, setBlankAnswers] = useState({})
    const [activeBlankIndex, setActiveBlankIndex] = useState(0)
    const [inputValue, setInputValue] = useState('')
    const [allSubmitted, setAllSubmitted] = useState(false)
    const pendingAnswersRef = useRef(null)
    const inlineInputRef = useRef(null)

    // ── Mistake tracking & wrong-chip flash (distractor/sentence builder) ──
    // Tracks count of unique wrong tries per blank (number, not boolean)
    const [mistakesByBlank, setMistakesByBlank] = useState({})
    const [flashingChip, setFlashingChip] = useState(null)
    const flashTimerRef = useRef(null)

    const triggerFlash = useCallback((identifier) => {
        setFlashingChip(identifier)
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
        flashTimerRef.current = setTimeout(() => setFlashingChip(null), 500)
    }, [])

    useEffect(() => {
        return () => {
            if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
            if (inputFlashTimerRef.current) clearTimeout(inputFlashTimerRef.current)
        }
    }, [])

    // ── Writing mode: attempt tracking per blank ──
    const [writingFailCount, setWritingFailCount] = useState(0)
    const [revealedAnswer, setRevealedAnswer] = useState(null)
    const [inputFlashing, setInputFlashing] = useState(false)
    const [inputFlashKey, setInputFlashKey] = useState(0)
    const inputFlashTimerRef = useRef(null)

    // Reset writing attempt state when active blank changes
    useEffect(() => {
        setWritingFailCount(0)
        setRevealedAnswer(null)
        setInputFlashing(false)
        setInputValue('')
    }, [activeBlankIndex])

    // ── Special characters helper keyboard (writing mode) ──
    // Scan all alternatives for non-ASCII letters and non-letter characters (hyphens, apostrophes, etc.)
    const specialChars = useMemo(() => {
        if (!isWritingMode) return []
        const chars = new Set()
        alternatives.forEach((alt) => {
            if (!alt.text) return
            for (const ch of alt.text) {
                const lower = ch.toLowerCase()
                if (/[^\x00-\x7F]/.test(ch) && /\p{L}/u.test(ch)) {
                    chars.add(lower)
                } else if (!/\p{L}/u.test(ch) && ch !== ' ' && ch.trim()) {
                    chars.add(ch)
                }
            }
        })
        return Array.from(chars).sort()
    }, [isWritingMode, alternatives])

    // All alternative texts for L2 keyboard letter filtering
    const allAlternativeTexts = useMemo(() => {
        return alternatives.filter((a) => a.text).map((a) => a.text)
    }, [alternatives])

    const handleKeyboardKeyPress = useCallback(
        (char) => {
            const input = inlineInputRef.current
            if (!input) {
                setInputValue((prev) => prev + char)
                return
            }
            const start = input.selectionStart ?? inputValue.length
            const end = input.selectionEnd ?? inputValue.length
            const newValue = inputValue.slice(0, start) + char + inputValue.slice(end)
            setInputValue(newValue)
            requestAnimationFrame(() => {
                input.focus()
                input.selectionStart = input.selectionEnd = start + char.length
            })
        },
        [inputValue]
    )

    const handleKeyboardBackspace = useCallback(() => {
        const input = inlineInputRef.current
        if (!input) {
            setInputValue((prev) => prev.slice(0, -1))
            return
        }
        const start = input.selectionStart ?? inputValue.length
        const end = input.selectionEnd ?? inputValue.length
        if (start !== end) {
            const newValue = inputValue.slice(0, start) + inputValue.slice(end)
            setInputValue(newValue)
            requestAnimationFrame(() => {
                input.focus()
                input.selectionStart = input.selectionEnd = start
            })
        } else if (start > 0) {
            const newValue = inputValue.slice(0, start - 1) + inputValue.slice(start)
            setInputValue(newValue)
            requestAnimationFrame(() => {
                input.focus()
                input.selectionStart = input.selectionEnd = start - 1
            })
        }
    }, [inputValue])

    const handleSpecialCharInsert = useCallback(
        (char) => {
            const input = inlineInputRef.current
            if (!input) return
            const start = input.selectionStart ?? inputValue.length
            const end = input.selectionEnd ?? inputValue.length
            const newValue = inputValue.slice(0, start) + char + inputValue.slice(end)
            setInputValue(newValue)
            requestAnimationFrame(() => {
                input.focus()
                input.selectionStart = input.selectionEnd = start + char.length
            })
        },
        [inputValue]
    )

    // ── Submit results once all blanks are filled ──
    const submitResults = useCallback(
        (finalAnswers) => {
            setAllSubmitted(true)
            pendingAnswersRef.current = finalAnswers
        },
        []
    )

    // ── Post-submission: feedback delay + optional PlayAfter audio + onComplete ──
    useEffect(() => {
        if (!allSubmitted || !pendingAnswersRef.current) return
        const finalAnswers = pendingAnswersRef.current
        let cancelled = false

        const finish = async () => {
            await new Promise((r) => setTimeout(r, 1200))
            if (cancelled) return

            if (shouldPlayAfter) {
                await audioPlayer.playAudio(audioBlobUrl)
                if (cancelled) return
            }

            const answers = blanks.map((_, idx) => ({
                testOptionId: testOption.testOptionId,
                answer: finalAnswers[idx]?.text || '',
                correct: finalAnswers[idx]?.correct || false,
                failCount: finalAnswers[idx]?.failCount || 0,
                wrongTryCount: finalAnswers[idx]?.wrongTryCount || 0,
            }))

            if (onComplete) {
                onComplete({ testOptionId: testOption.testOptionId, answers })
            }
        }

        finish()
        return () => {
            cancelled = true
            audioPlayer.stopAudio()
        }
    }, [allSubmitted])

    // ── Advance to the next unfilled blank, or submit ──
    const advanceOrSubmit = useCallback(
        (newAnswers) => {
            const nextUnfilled = blanks.findIndex((_, idx) => idx > activeBlankIndex && !newAnswers[idx])
            if (nextUnfilled >= 0) {
                setActiveBlankIndex(nextUnfilled)
            } else if (!isExamMode) {
                submitResults(newAnswers)
            }
            // Exam mode: don't auto-submit — user reviews and clicks Submit
        },
        [blanks, activeBlankIndex, submitResults, isExamMode]
    )

    // ── Distractor mode: tap chip ──
    const handleChipTap = useCallback(
        (alt) => {
            if (allSubmitted || flashingChip) return

            if (isExamMode) {
                // Exam: accept any answer
                const newAnswers = { ...blankAnswers, [activeBlankIndex]: { text: alt.text, correct: alt.correct } }
                setBlankAnswers(newAnswers)
                advanceOrSubmit(newAnswers)
                return
            }

            // Normal: reject wrong answers with flash, score as mistake
            if (!alt.correct) {
                setMistakesByBlank((prev) => ({
                    ...prev,
                    [activeBlankIndex]: (prev[activeBlankIndex] || 0) + 1,
                }))
                triggerFlash(alt.text)
                return
            }

            // Correct chip: place it, include wrongTryCount for scoring
            const wrongTryCount = mistakesByBlank[activeBlankIndex] || 0
            const newAnswers = {
                ...blankAnswers,
                [activeBlankIndex]: { text: alt.text, correct: wrongTryCount === 0, wrongTryCount },
            }
            setBlankAnswers(newAnswers)
            advanceOrSubmit(newAnswers)
        },
        [blankAnswers, activeBlankIndex, allSubmitted, isExamMode, flashingChip, mistakesByBlank, triggerFlash, advanceOrSubmit]
    )

    // ── Sentence builder mode: tap pooled chip ──
    const [usedChipIds, setUsedChipIds] = useState(new Set())

    const handlePooledChipTap = useCallback(
        (chip) => {
            if (allSubmitted || usedChipIds.has(chip.chipId) || flashingChip) return

            const correctForBlank = (alternativesByBlank[activeBlankIndex] || []).some(
                (a) => a.correct && a.text === chip.text
            )

            if (isExamMode) {
                // Exam: accept any answer
                const newAnswers = {
                    ...blankAnswers,
                    [activeBlankIndex]: { text: chip.text, correct: correctForBlank },
                }
                setBlankAnswers(newAnswers)
                setUsedChipIds((prev) => new Set([...prev, chip.chipId]))
                advanceOrSubmit(newAnswers)
                return
            }

            // Normal: reject wrong answers with flash, score as mistake
            if (!correctForBlank) {
                setMistakesByBlank((prev) => ({
                    ...prev,
                    [activeBlankIndex]: (prev[activeBlankIndex] || 0) + 1,
                }))
                triggerFlash(chip.chipId)
                return
            }

            // Correct chip: place it, include wrongTryCount for scoring
            const wrongTryCount = mistakesByBlank[activeBlankIndex] || 0
            const newAnswers = {
                ...blankAnswers,
                [activeBlankIndex]: { text: chip.text, correct: wrongTryCount === 0, wrongTryCount },
            }
            setBlankAnswers(newAnswers)
            setUsedChipIds((prev) => new Set([...prev, chip.chipId]))
            advanceOrSubmit(newAnswers)
        },
        [blankAnswers, activeBlankIndex, allSubmitted, usedChipIds, isExamMode, flashingChip, mistakesByBlank, alternativesByBlank, triggerFlash, advanceOrSubmit]
    )

    // ── Exam mode: manual submit & reset ──
    const examButtonRowRef = useRef(null)
    const allBlanksFilled = blanks.every((_, i) => blankAnswers[i])

    useEffect(() => {
        if (isExamMode && allBlanksFilled && !allSubmitted && examButtonRowRef.current) {
            examButtonRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }
    }, [isExamMode, allBlanksFilled, allSubmitted])

    const handleExamSubmit = useCallback(() => {
        submitResults(blankAnswers)
    }, [blankAnswers, submitResults])

    const handleExamReset = useCallback(() => {
        setBlankAnswers({})
        setActiveBlankIndex(0)
        setUsedChipIds(new Set())
    }, [])

    // ── Writing mode: submit answer ──
    const handleWritingSubmit = useCallback(() => {
        if (!inputValue.trim() || inputFlashing) return
        const normalized = inputValue.trim().toLowerCase()
        const blankAlts = alternativesByBlank[activeBlankIndex] || []
        const correctAlt = blankAlts.find((a) => a.correct)
        const isCorrect = blankAlts.some((a) => a.correct && a.text.trim().toLowerCase() === normalized)

        if (isExamMode) {
            // Exam mode: accept any answer immediately
            const newAnswers = { ...blankAnswers, [activeBlankIndex]: { text: inputValue.trim(), correct: isCorrect } }
            setBlankAnswers(newAnswers)
            setInputValue('')
            advanceOrSubmit(newAnswers)
            return
        }

        if (isCorrect) {
            // Correct: include failCount for Unity-matching penalty calculation
            const newAnswers = {
                ...blankAnswers,
                [activeBlankIndex]: {
                    text: inputValue.trim(),
                    correct: writingFailCount === 0,
                    failCount: writingFailCount,
                },
            }
            setBlankAnswers(newAnswers)
            setInputValue('')
            advanceOrSubmit(newAnswers)
            return
        }

        // Wrong answer: flash red on the inline input
        setInputFlashing(true)
        setInputFlashKey((k) => k + 1)
        if (inputFlashTimerRef.current) clearTimeout(inputFlashTimerRef.current)
        inputFlashTimerRef.current = setTimeout(() => setInputFlashing(false), 500)
        setMistakesByBlank((prev) => ({
            ...prev,
            [activeBlankIndex]: (prev[activeBlankIndex] || 0) + 1,
        }))

        const newFailCount = writingFailCount + 1
        setWritingFailCount(newFailCount)
        setInputValue('')

        if (newFailCount >= 2) {
            // After 2 failures: reveal the word from the original sentence so the user can type it
            const blank = blanks[activeBlankIndex]
            const originalWord = blank && blank.startIndex >= 0 && sentenceText
                ? sentenceText.slice(blank.startIndex, blank.startIndex + blank.length)
                : correctAlt?.text
            if (originalWord) setRevealedAnswer(originalWord)
        }
    }, [inputValue, inputFlashing, blankAnswers, activeBlankIndex, alternativesByBlank, blanks, sentenceText, isExamMode, writingFailCount, advanceOrSubmit])

    // ── Active blank's chip alternatives (for distractor mode) ──
    const activeChips = alternativesByBlank[activeBlankIndex] || []

    if (!ready) return null

    return (
        <div className={classes.container}>
            {!isNativeSpeaker && testOption.explanationText && (
                <Typography className={classes.questionText} style={{ textAlign }}>
                    {renderFormattedText(testOption.explanationText)}
                </Typography>
            )}

            {/* Sentence with blanks (active blank becomes inline input in writing mode) */}
            <div className={classes.sentenceContainer} style={{ justifyContent: textAlign === 'right' ? 'flex-end' : textAlign === 'left' ? 'flex-start' : 'center' }}>
                {allSubmitted && isSentenceBuilder &&
                 mergedSentenceWords.length > 0 &&
                 blanks.every((_, i) => blankAnswers[i]?.correct)
                    ? mergedSentenceWords.map((word, idx) => (
                          <span
                              key={`merged-${idx}`}
                              className={`${classes.blankSlot} ${classes.blankSlotCorrect}`}
                          >
                              {word}
                          </span>
                      ))
                    : sentenceParts.map((part, idx) => {
                          if (part.type === 'text') {
                              return (
                                  <span key={idx} className={classes.sentenceWord}>
                                      {renderFormattedText(part.value)}
                                  </span>
                              )
                          }

                          const bi = part.blankIndex
                          const answer = blankAnswers[bi]
                          const isActive = bi === activeBlankIndex && !allSubmitted

                          // Writing mode: active blank becomes an inline input (only if not yet answered)
                          if (isWritingMode && isActive && !answer) {
                              const inputClassName = inputFlashing
                                  ? `${classes.inlineInput} ${classes.inlineInputWrong}`
                                  : classes.inlineInput
                              return (
                                  <input
                                      key={`input-${bi}-${inputFlashKey}`}
                                      ref={inlineInputRef}
                                      className={inputClassName}
                                      type="text"
                                      inputMode={isTouchDevice ? 'none' : undefined}
                                      value={inputValue}
                                      onChange={(e) => setInputValue(e.target.value)}
                                      onKeyDown={(e) => e.key === 'Enter' && handleWritingSubmit()}
                                      style={{ width: `${Math.max(6, inputValue.length + 2)}ch` }}
                                      autoFocus
                                  />
                              )
                          }

                          let slotClass = classes.blankSlot
                          if (allSubmitted && answer) {
                              if (isExamMode) {
                                  slotClass += ` ${classes.blankSlotExam}`
                              } else {
                                  // Normal mode: always green — wrong answers were rejected,
                                  // so the placed answer is always the correct word
                                  slotClass += ` ${classes.blankSlotCorrect}`
                              }
                          } else if (answer) {
                              slotClass += ` ${classes.blankSlotFilled}`
                          } else if (isActive) {
                              slotClass += ` ${classes.blankSlotActive}`
                          }

                          return (
                              <span key={idx} className={slotClass}>
                                  {answer?.text || '\u00A0\u00A0\u00A0\u00A0\u00A0'}
                              </span>
                          )
                      })}
            </div>

            {/* Writing mode: revealed answer hint, keyboard, submit button */}
            {isWritingMode && !allSubmitted && !(isExamMode && allBlanksFilled) && (
                <>
                    {revealedAnswer && (
                        <Typography className={classes.revealedHint}>
                            Type: <span className={classes.revealedWord}>{revealedAnswer}</span>
                        </Typography>
                    )}
                    {isTouchDevice ? (
                        <L2OnScreenKeyboard
                            languageId={l2LanguageId}
                            correctAnswers={allAlternativeTexts}
                            onKeyPress={handleKeyboardKeyPress}
                            onBackspace={handleKeyboardBackspace}
                            onSubmit={handleWritingSubmit}
                            inputValue={inputValue}
                            submitLabel={isExamMode ? intl.formatMessage(playerMessages.submit) : intl.formatMessage(playerMessages.check)}
                            submitDisabled={inputFlashing}
                            isExamMode={isExamMode}
                        />
                    ) : (
                        <>
                            {specialChars.length > 0 && (
                                <div className={classes.helperKeyboard}>
                                    {specialChars.map((ch) => (
                                        <button
                                            key={ch}
                                            className={classes.helperKey}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleSpecialCharInsert(ch)}
                                        >
                                            {ch}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className={classes.writeSubmitRow}>
                                <button
                                    className={classes.writeSubmitButton}
                                    onClick={handleWritingSubmit}
                                    disabled={!inputValue.trim() || inputFlashing}
                                >
                                    {isExamMode ? intl.formatMessage(playerMessages.submit) : intl.formatMessage(playerMessages.check)}
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}

            {/* Distractor / sentence builder chips */}
            {!isWritingMode && !allSubmitted && (
                <div className={classes.chipsContainer}>
                    {isSentenceBuilder
                        ? pooledChips.map((chip) => {
                              const isUsed = usedChipIds.has(chip.chipId)
                              const isFlashing = flashingChip === chip.chipId
                              let chipClass = classes.chip
                              if (isFlashing) {
                                  chipClass += ` ${classes.chipFlashWrong}`
                              } else if (isUsed) {
                                  chipClass += ` ${classes.chipUsed}`
                              }
                              return (
                                  <button
                                      key={chip.chipId}
                                      className={chipClass}
                                      onClick={() => handlePooledChipTap(chip)}
                                      disabled={isUsed && !isFlashing}
                                  >
                                      {chip.text}
                                  </button>
                              )
                          })
                        : activeChips.map((alt, idx) => {
                              const isUsed = Object.values(blankAnswers).some((a) => a.text === alt.text)
                              const isFlashing = flashingChip === alt.text
                              let chipClass = classes.chip
                              if (isFlashing) {
                                  chipClass += ` ${classes.chipFlashWrong}`
                              } else if (isUsed) {
                                  chipClass += ` ${classes.chipUsed}`
                              }
                              return (
                                  <button
                                      key={idx}
                                      className={chipClass}
                                      onClick={() => handleChipTap(alt)}
                                      disabled={isUsed && !isFlashing}
                                  >
                                      {alt.text}
                                  </button>
                              )
                          })}
                </div>
            )}

            {/* Exam mode: Submit / Reset buttons after all blanks filled */}
            {isExamMode && allBlanksFilled && !allSubmitted && (
                <div ref={examButtonRowRef} className={classes.examButtonRow}>
                    <button className={classes.resetButton} onClick={handleExamReset}>
                        <FormattedMessage {...playerMessages.reset} />
                    </button>
                    <button className={classes.submitButton} onClick={handleExamSubmit}>
                        <FormattedMessage {...playerMessages.submit} />
                    </button>
                </div>
            )}

            {/* Exam mode: show submitted answers feedback */}
            {allSubmitted && isExamMode && (
                <Typography className={`${classes.feedbackText} ${classes.feedbackCorrect}`}>
                    <FormattedMessage {...playerMessages.answerSubmitted} />
                </Typography>
            )}
        </div>
    )
}

export default FillInBlankInteraction
